import { __rest } from 'tslib';
import React__default from 'react';
import RcInputNumber from 'rc-input-number';
import { Body } from '../Typography/index.es.js';
import { UpOutlined, DownOutlined } from '@teko/ds-icon';
import { Shape } from '../Shape/index.es.js';
import { MAPPING_SIZE } from '../../constants/index.es.js';
import { SizeContext } from '../../contexts/SizeContext.es.js';
import styled from '@emotion/styled';

const SAVED_DECIMAL_SEPARATOR = '.';
const InputNumber = React__default.forwardRef((_a, ref) => {
    var _b, _c;
    var { size: customizeSize, addonBefore, addonAfter, bordered = true, disabled = false, readOnly = false } = _a, props = __rest(_a, ["size", "addonBefore", "addonAfter", "bordered", "disabled", "readOnly"]);
    const size = React__default.useContext(SizeContext);
    const mergeSize = customizeSize || size || 'm';
    const { decimalSeparator } = props;
    const formatter = (value) => {
        if (!value)
            return;
        const locale = decimalSeparator === '.' ? 'en-CA' : 'de-DE';
        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: 10,
        }).format(value);
    };
    const parser = (value) => {
        const thousandSeparator = decimalSeparator === '.' ? ',' : '.';
        return value
            .replace(thousandSeparator, '')
            .replace(decimalSeparator, SAVED_DECIMAL_SEPARATOR);
    };
    const prefix = addonBefore ? (React__default.createElement(StyledSpace, { height: MAPPING_SIZE[mergeSize] }, typeof addonBefore === 'string' ? (React__default.createElement(Body, { color: "textSecondary", level: 1, weight: "regular" }, addonBefore)) : (React__default.cloneElement(addonBefore, {
        size: ((_b = addonBefore === null || addonBefore === void 0 ? void 0 : addonBefore.props) === null || _b === void 0 ? void 0 : _b.size) || 20,
    })))) : null;
    const suffix = addonAfter ? (React__default.createElement(StyledSpace, { height: MAPPING_SIZE[mergeSize], marginLeft: "s16" }, typeof addonAfter === 'string' ? (React__default.createElement(Body, { color: "textSecondary", level: 1, weight: "regular" }, addonAfter)) : (React__default.cloneElement(addonAfter, {
        size: ((_c = addonAfter === null || addonAfter === void 0 ? void 0 : addonAfter.props) === null || _c === void 0 ? void 0 : _c.size) || 20,
    })))) : null;
    return (React__default.createElement(Wrapper, { height: MAPPING_SIZE[mergeSize], disabled: disabled, readOnly: readOnly, bordered: bordered, paddingRight: !!suffix ? 's16' : 's0', className: "wrap-input-number" },
        prefix,
        React__default.createElement(RcInputNumber, Object.assign({ ref: ref, upHandler: React__default.createElement(UpOutlined, { size: 12 }), downHandler: React__default.createElement(DownOutlined, { size: 12 }), disabled: disabled, readOnly: readOnly, formatter: formatter, parser: parser }, props)),
        suffix));
});
InputNumber.displayName = 'InputNumber';
const Wrapper = styled(Shape)(({ theme, disabled, readOnly, bordered }) => `
  ${disabled
    ? `background: ${theme.colors.background}; 
         cursor: not-allowed;`
    : 'background: white;'}
  ${bordered
    ? `border: 1px solid ${theme.colors.border};
         border-radius: 4px;`
    : ''}
  &:focus-within{
    border-color: ${theme.colors.link500};
  }
  display: flex;
  padding-left: 0.75rem;
  .rc-input-number {
      position: relative;
      flex: 1;
      padding: 0 1px;
      &:hover{
        .rc-input-number-handler-wrap{
            opacity: 1;
        }
      }
  }
  .rc-input-number-input-wrap{
      height: 100%;
      & input {
        width: 100%;
        height: 100%;
        outline: 0;
        border: 0;
        &::placeholder {
          color: ${theme.colors.placeholder};
        }
        ${disabled ? 'cursor: not-allowed;' : ''}
    }
  }
  .rc-input-number-handler-wrap{
      opacity: 0;
      transition: opacity 0.24s linear 0.1s;
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: center;
      right: 0;
      line-height: 0;
      height: 100%;
      width: 25px;
      ${bordered
    ? `border-left: 1px solid ${theme.colors.border};
             border-right: 1px solid ${theme.colors.border};`
    : ''}
      ${disabled || readOnly ? 'display: none;' : ''}
  }
  .rc-input-number-handler-up, 
  .rc-input-number-handler-down{
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover path {
      stroke: ${theme.colors.link500};
    }
  }
  .rc-input-number-handler-down {
    ${bordered ? `border-top: 1px solid ${theme.colors.border};` : ''}
  }
  .rc-input-number-handler-up-disabled, 
  .rc-input-number-handler-down-disabled {
    cursor: not-allowed;
    background: ${theme.colors.background};
    &:hover path {
      stroke: ${theme.colors.textSecondary};
    }
  }
`);
const StyledSpace = styled(Shape) `
  display: flex;
  align-items: center;
`;

export { InputNumber };
